import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import Alert from '../components/ui/Alert';
import { useDocuments } from '../contexts/DocumentContext';
import { useAuth } from '../contexts/AuthContext';
import { validateEmail, sendDocumentForSigning } from '../utils/emailService';
import './SendPage.css';

const SendPage = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const { documents, markDocumentAsSent, loading, error: contextError } = useDocuments();
  const { user } = useAuth();
  
  const [document, setDocument] = useState(null);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipients, setRecipients] = useState([]);
  const [selfSign, setSelfSign] = useState(false);
  const [includeOwner, setIncludeOwner] = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const location = useLocation();
  
  // Load document data and handle recipient emails from EditorPage
  useEffect(() => {
    if (documents.length > 0) {
      const doc = documents.find(d => d.id === documentId);
      if (doc) {
        setDocument(doc);
        
        // Set default subject and message
        setSubject(`Please sign: ${doc.name}`);
        setMessage(`I've sent you a document to sign: ${doc.name}. Please review and sign at your earliest convenience.`);
        
        // Check if we have recipients from the editor page
        if (location.state && location.state.recipients && location.state.fromEditor) {
          // Use the recipients from the editor page
          setRecipients(location.state.recipients.map(email => email || ''));
          
          // Check if owner should be included as a signer
          if (location.state.includeOwner) {
            setIncludeOwner(true);
            setSelfSign(true);
          }
        } 
        // Otherwise initialize with one empty recipient if needed
        else if (recipients.length === 0) {
          setRecipients(['']);
        }
      }
    }
  }, [documentId, documents, recipients.length, location.state]);

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    // Validate recipients
    if (recipients.length === 0 && !recipientEmail && !selfSign) {
      newErrors.recipients = 'At least one recipient or self-sign is required';
    }
    
    // Validate each recipient email
    recipients.forEach((email, index) => {
      if (!email && recipients.length > 0) {
        newErrors[`recipient${index}`] = 'Email is required';
      } else if (email && !validateEmail(email)) {
        newErrors[`recipient${index}`] = 'Invalid email format';
      }
    });
    
    // For backward compatibility
    if (recipientEmail && !validateEmail(recipientEmail)) {
      newErrors.recipientEmail = 'Please enter a valid email address';
    }
    
    if (!subject) {
      newErrors.subject = 'Subject is required';
    }
    
    if (!message) {
      newErrors.message = 'Message is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Get the current user's information from the component level
      const senderName = user?.user_metadata?.full_name || user?.email || 'Document Sender';
      const senderEmail = user?.email;
      
      // Get the document file data if available
      let fileData = null;
      let fileName = null;
      
      if (document) {
        if (document.file_data) {
          fileData = document.file_data;
        } else if (document.fileData) {
          fileData = document.fileData;
        }
        
        fileName = document.name || 'document.pdf';
      }
      
      // Combine all recipients (including legacy recipientEmail if present)
      const allRecipients = [...recipients.filter(r => r)];
      if (recipientEmail) {
        allRecipients.push(recipientEmail);
      }
      
      // Send to each recipient
      for (const recipient of allRecipients) {
        console.log('Sending document to:', recipient);
        
        // Send document for signing using the server API
        const result = await sendDocumentForSigning({
          documentId,
          recipientEmail: recipient,
          subject,
          message,
          senderName,
          senderEmail,
          fileData,
          fileName
        });
        
        console.log('Send result:', result);
        
        // Mark document as sent in context
        await markDocumentAsSent(documentId, recipient);
      }
      
      // If self-sign is enabled, navigate to sign page
      if (selfSign) {
        setSuccess(true);
        setIsLoading(false);
        
        // Redirect after a short delay
        setTimeout(() => {
          navigate(`/sign/${documentId}`);
        }, 1500);
      } else {
        setSuccess(true);
        setIsLoading(false);
        
        // Redirect after a short delay
        setTimeout(() => {
          navigate('/dashboard');
        }, 1500);
      }
    } catch (err) {
      console.error('Error sending document:', err);
      setError(`Failed to send document: ${err.message || 'Please try again.'}`);
      setIsLoading(false);
    }
  };

  // Combined loading and document check to prevent flashing error messages
  if (loading || (!document && documents.length > 0)) {
    return (
      <div className="send-page">
        <div className="container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading document...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || contextError) {
    return (
      <div className="send-page">
        <div className="container">
          <Alert 
            type="error" 
            title="Error" 
            message={error || contextError} 
          />
          <div className="text-center mt-lg">
            <Button onClick={() => navigate(-1)}>
              Go Back
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Only show "Document Not Found" after we're sure it's not still loading
  if (!document && !loading && documents.length > 0) {
    return (
      <div className="send-page">
        <div className="container">
          <Alert 
            type="error" 
            title="Document Not Found" 
            message="The document you're looking for doesn't exist or has been deleted." 
          />
          <div className="text-center mt-lg">
            <Button onClick={() => navigate('/upload')}>
              Upload a Document
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // If document is not yet loaded, show loading
  if (!document) {
    return (
      <div className="send-page">
        <div className="container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Preparing document for sending...</p>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="send-page">
        <div className="container">
          <Card className="success-card">
            <div className="success-icon">✓</div>
            <h2 className="success-title">Document Sent Successfully!</h2>
            <p className="success-message">
              {includeOwner
                ? "Your document has been sent to recipients. You'll be redirected to sign it as well."
                : selfSign 
                  ? "Your document has been sent to recipients. You'll be redirected to sign it yourself."
                  : "Your document has been sent for signing. You'll be notified when it's signed."}
            </p>
            <div className="success-actions">
              <Button onClick={() => navigate('/dashboard')}>
                Go to Dashboard
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="send-page">
      <div className="container">
        <div className="send-header">
          <h1 className="page-title">Send Document for Signature</h1>
          <p className="page-subtitle">
            Enter recipient details and customize your message
          </p>
        </div>
        
        <Card className="send-card">
          <div className="document-info">
            <h3 className="document-name">{document.name}</h3>
            <p className="document-meta">
              {document.fields?.length || 0} signature fields added
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="send-form">
            <div className="form-group">
              <label className="form-label">
                Recipients <span className="input-required">*</span>
              </label>
              
              <div className="recipients-container">
                {recipients.map((recipient, index) => (
                  <div key={index} className="recipient-item">
                    <input
                      type="email"
                      value={recipient}
                      onChange={(e) => {
                        const newRecipients = [...recipients];
                        newRecipients[index] = e.target.value;
                        setRecipients(newRecipients);
                      }}
                      placeholder="Enter recipient's email address"
                      className={`form-control ${errors[`recipient${index}`] ? 'input-error' : ''}`}
                    />
                    {recipients.length > 1 && (
                      <button 
                        type="button"
                        className="recipient-remove-btn"
                        onClick={() => {
                          const newRecipients = [...recipients];
                          newRecipients.splice(index, 1);
                          setRecipients(newRecipients);
                        }}
                      >
                        ×
                      </button>
                    )}
                    {errors[`recipient${index}`] && (
                      <div className="input-error-message">{errors[`recipient${index}`]}</div>
                    )}
                  </div>
                ))}
                
                <button 
                  type="button"
                  className="add-recipient-btn"
                  onClick={() => setRecipients([...recipients, ''])}
                >
                  + Add Recipient
                </button>
              </div>
              
              {errors.recipients && (
                <div className="input-error-message">{errors.recipients}</div>
              )}
            </div>
            
            <div className="form-group">
              <div className="checkbox-group">
                <input
                  type="checkbox"
                  id="selfSign"
                  checked={selfSign}
                  onChange={(e) => setSelfSign(e.target.checked)}
                />
                <label htmlFor="selfSign">
                  I want to sign this document myself
                </label>
              </div>
              {selfSign && !includeOwner && (
                <div className="self-sign-note">
                  You'll be able to sign the document after sending it to other recipients.
                </div>
              )}
              
              {includeOwner && (
                <div className="self-sign-note highlight">
                  <strong>You and Others Mode:</strong> You'll sign this document along with the other recipients.
                </div>
              )}
            </div>
            
            <div className="form-group">
              <Input
                label="Subject"
                type="text"
                id="subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Enter email subject"
                error={errors.subject}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="message" className="form-label">
                Message <span className="input-required">*</span>
              </label>
              <textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Enter your message to the recipient"
                className={`form-textarea ${errors.message ? 'input-error' : ''}`}
                rows={5}
                required
              ></textarea>
              {errors.message && (
                <div className="input-error-message">{errors.message}</div>
              )}
            </div>
            
            <div className="send-actions">
              <Button 
                variant="secondary" 
                type="button" 
                onClick={() => navigate(-1)}
              >
                Back
              </Button>
              <Button 
                type="submit" 
                loading={isLoading}
                disabled={isLoading}
              >
                {includeOwner ? 'Send & Sign Together' : 
                 selfSign ? 'Send & Sign Myself' : 
                 'Send for Signature'}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default SendPage;
